import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Plus, Pencil, Trash2, Loader2, FolderTree, GitMerge } from 'lucide-react';
import { flattenTree, CATEGORY_COLORS } from './categoryUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const NONE = '__none__';

export const CategoryManagerDialog = ({ open, onOpenChange, categories, onChanged }) => {
  const [form, setForm] = useState({ name: '', shortname: '', parent_id: NONE, color: '' });
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [mergeSource, setMergeSource] = useState(null);
  const [mergeTarget, setMergeTarget] = useState('');
  const [merging, setMerging] = useState(false);
  const tree = flattenTree(categories);

  const doMerge = async () => {
    if (!mergeSource || !mergeTarget) { toast.error('Pick the category to merge into'); return; }
    if (!window.confirm(`Merge "${mergeSource.name}" into "${tree.find(c => c.id === mergeTarget)?.name}"? Its capabilities and child categories move across and "${mergeSource.name}" is deleted.`)) return;
    setMerging(true);
    try {
      const res = await axios.post(`${API_URL}/api/rfp/categories/${mergeSource.id}/merge/${mergeTarget}`, {}, { withCredentials: true });
      toast.success(`Merged into "${res.data.target}" — ${res.data.blocks_updated} capabilit${res.data.blocks_updated === 1 ? 'y' : 'ies'} re-homed`);
      setMergeSource(null);
      setMergeTarget('');
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Merge failed');
    } finally { setMerging(false); }
  };

  const reset = () => { setForm({ name: '', shortname: '', parent_id: NONE, color: '' }); setEditingId(null); };

  const submit = async () => {
    if (!form.name.trim()) { toast.error('Category name is required'); return; }
    if (!/^[A-Za-z]{2,6}$/.test(form.shortname.trim())) { toast.error('Shortname must be 2-6 letters (e.g. ECOM)'); return; }
    setBusy(true);
    const payload = { name: form.name.trim(), shortname: form.shortname.trim().toUpperCase(), parent_id: form.parent_id === NONE ? '' : form.parent_id, color: form.color || '' };
    try {
      if (editingId) {
        await axios.put(`${API_URL}/api/rfp/categories/${editingId}`, payload, { withCredentials: true });
        toast.success(`"${payload.name}" updated`);
      } else {
        await axios.post(`${API_URL}/api/rfp/categories`, payload, { withCredentials: true });
        toast.success(`"${payload.name}" created`);
      }
      reset();
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save the category');
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (c) => {
    setEditingId(c.id);
    setForm({ name: c.name, shortname: c.shortname, parent_id: c.parent_id || NONE, color: c.color || '' });
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete category "${c.name}"? It will be removed from any capabilities using it.`)) return;
    try {
      await axios.delete(`${API_URL}/api/rfp/categories/${c.id}`, { withCredentials: true });
      toast.success(`"${c.name}" deleted`);
      if (editingId === c.id) reset();
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not delete the category');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-w-lg" data-testid="rfp-category-manager">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FolderTree className="w-4 h-4 text-[#4ABFCF]" />Capability Categories
          </DialogTitle>
        </DialogHeader>

        <div className="border border-slate-200 rounded-md divide-y divide-slate-100 max-h-64 overflow-y-auto">
          {tree.length === 0 ? (
            <p className="text-xs text-slate-400 p-3">No categories yet.</p>
          ) : tree.map(c => (
            <div key={c.id} className="flex items-center gap-2 px-2 py-1.5 text-xs hover:bg-slate-50"
              style={{ paddingLeft: `${8 + c.depth * 16}px` }} data-testid={`rfp-cat-row-${c.id}`}>
              <span className="w-2.5 h-2.5 rounded-full border border-slate-200 shrink-0"
                style={{ backgroundColor: c.color || '#E2E8F0' }} title={c.color || 'No colour set'} />
              <span className="font-medium text-[#0F2040] truncate">{c.name}</span>
              <span className="font-mono text-[9px] text-slate-400 bg-slate-50 border border-slate-200 rounded px-1">{c.shortname}</span>
              {typeof c.block_count === 'number' && (
                <span className="text-[9px] text-slate-400">{c.block_count} capabilit{c.block_count === 1 ? 'y' : 'ies'}</span>
              )}
              <span className="flex-1" />
              <button onClick={() => { setMergeSource(c); setMergeTarget(''); }} className="text-slate-400 hover:text-[#4ABFCF]"
                data-testid={`rfp-cat-merge-${c.id}`} title="Merge this category into another"><GitMerge className="w-3 h-3" /></button>
              <button onClick={() => startEdit(c)} className="text-slate-400 hover:text-[#0F2040]"
                data-testid={`rfp-cat-edit-${c.id}`} title="Edit category"><Pencil className="w-3 h-3" /></button>
              <button onClick={() => remove(c)} className="text-slate-400 hover:text-red-600"
                data-testid={`rfp-cat-delete-${c.id}`} title="Delete category"><Trash2 className="w-3 h-3" /></button>
            </div>
          ))}
        </div>

        {mergeSource && (
          <div className="border border-cyan-200 bg-cyan-50/50 rounded-md p-3 space-y-2" data-testid="rfp-cat-merge-panel">
            <p className="text-xs font-semibold text-[#0F2040] flex items-center gap-1.5">
              <GitMerge className="w-3.5 h-3.5 text-[#4ABFCF]" />Merge "{mergeSource.name}" into…
            </p>
            <div className="flex gap-2 items-center">
              <Select value={mergeTarget} onValueChange={setMergeTarget}>
                <SelectTrigger className="flex-1 h-8 text-xs" data-testid="rfp-cat-merge-target-select">
                  <SelectValue placeholder="Choose the surviving category" />
                </SelectTrigger>
                <SelectContent>
                  {tree.filter(c => c.id !== mergeSource.id).map(c => (
                    <SelectItem key={c.id} value={c.id}>{'\u00A0'.repeat(c.depth * 3)}{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => setMergeSource(null)}>Cancel</Button>
              <Button size="sm" onClick={doMerge} disabled={merging || !mergeTarget}
                className="h-8 text-xs bg-[#0F2040] hover:bg-[#1A3055] text-white" data-testid="rfp-cat-merge-confirm-btn">
                {merging ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <GitMerge className="w-3 h-3 mr-1" />}Merge
              </Button>
            </div>
            <p className="text-[9px] text-slate-500">Capabilities and child categories move to the surviving category; "{mergeSource.name}" is then deleted. Existing capability IDs are not renamed.</p>
          </div>
        )}

        <div className="border border-slate-200 rounded-md p-3 space-y-2">
          <p className="text-xs font-semibold text-[#0F2040]">{editingId ? 'Edit category' : 'New category'}</p>
          <div className="flex gap-2">
            <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="Name (e.g. eCommerce)" className="flex-1 h-8 text-xs" data-testid="rfp-cat-name-input" />
            <Input value={form.shortname} onChange={e => setForm(f => ({ ...f, shortname: e.target.value.toUpperCase().slice(0, 6) }))}
              placeholder="ECOM" className="w-20 h-8 text-xs font-mono uppercase" data-testid="rfp-cat-shortname-input" />
          </div>
          <div className="flex items-center gap-1.5 flex-wrap" data-testid="rfp-cat-color-picker">
            <span className="text-[10px] text-slate-500 mr-1">Chip colour:</span>
            <button onClick={() => setForm(f => ({ ...f, color: '' }))}
              className={`w-5 h-5 rounded-full border-2 text-[8px] text-slate-400 ${!form.color ? 'border-[#0F2040]' : 'border-slate-200'}`}
              title="Automatic" data-testid="rfp-cat-color-auto">A</button>
            {CATEGORY_COLORS.map(col => (
              <button key={col} onClick={() => setForm(f => ({ ...f, color: col }))}
                className={`w-5 h-5 rounded-full border-2 ${form.color === col ? 'border-[#0F2040] scale-110' : 'border-transparent'}`}
                style={{ backgroundColor: col }} title={col} data-testid={`rfp-cat-color-${col.slice(1)}`} />
            ))}
          </div>
          <div className="flex gap-2 items-center">
            <Select value={form.parent_id} onValueChange={v => setForm(f => ({ ...f, parent_id: v }))}>
              <SelectTrigger className="flex-1 h-8 text-xs" data-testid="rfp-cat-parent-select">
                <SelectValue placeholder="Parent category (optional)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>No parent (top level)</SelectItem>
                {tree.filter(c => c.id !== editingId).map(c => (
                  <SelectItem key={c.id} value={c.id}>{'\u00A0'.repeat(c.depth * 3)}{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {editingId && (
              <Button variant="outline" size="sm" className="h-8 text-xs" onClick={reset}>Cancel</Button>
            )}
            <Button size="sm" onClick={submit} disabled={busy}
              className="h-8 text-xs bg-[#0F2040] hover:bg-[#1A3055] text-white" data-testid="rfp-cat-save-btn">
              {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Plus className="w-3 h-3 mr-1" />}
              {editingId ? 'Save' : 'Add'}
            </Button>
          </div>
          <p className="text-[9px] text-slate-400">
            The 4-letter shortname prefixes capability IDs (e.g. ECOM-001). Changing it later doesn't rename existing IDs.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};
